"use client";

import { useState, useRef, useCallback, useEffect, memo, lazy, Suspense } from "react";
import { Link } from "@/i18n/routing";
import { useRouter } from "next/navigation";

const QuickViewModal = lazy(() => import("./QuickViewModal"));
import FallbackImage from "@/components/ui/FallbackImage";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/utils";
import { Badge } from "@/components/ui/Badge";
import { useTranslations } from "next-intl";
import { useCart } from "@/context/CartContext";
import { useToast } from "@/context/ToastContext";
import { useTheme } from "@/context/ThemeContext";
import { Heart, ShoppingBag, Check, Bell } from "lucide-react";
import { trackEvent } from "@/lib/track";
import { useWishlist } from "@/context/WishlistContext";

export interface ProductCardProduct {
  id: string;
  name: string;
  slug: string;
  price: number;
  comparePrice?: number | null;
  image?: string | null;
  isNew?: boolean;
  inStock?: boolean;
  featured?: boolean;
  createdAt?: string | Date | null;
  category?: "wigs" | "toppers" | "accessories";
  /** Pre-order: when true, the product can be added to cart even with zero
   *  stock. The shipping/charge happens normally — only the fulfillment is
   *  delayed until preorderShipDate. */
  preorderEnabled?: boolean;
  preorderShipDate?: string | Date | null;
  /** Consignment availability — wigs currently at resellers. When > 0
   *  and the platform flag is on, the product stays shoppable even
   *  with stockCount=0 (recall fires at checkout). */
  consignmentAvailableQty?: number;
  /** Pre-computed by the API: (inStock || preorder || consigned > 0). */
  effectivelyInStock?: boolean;
}

export interface ProductCardProps {
  product: ProductCardProduct;
  className?: string;
  /** Above-the-fold cards (first grid row) set this so their image is
   *  preloaded instead of lazy-loaded — improves LCP on listing pages. */
  priority?: boolean;
}

export const ProductCard = memo(function ProductCard({ product, className, priority }: ProductCardProps) {
  const {
    name,
    slug,
    price,
    comparePrice,
    image,
    isNew = false,
    inStock = true,
    featured = false,
    createdAt,
    category = "wigs",
    preorderEnabled = false,
    consignmentAvailableQty = 0,
    effectivelyInStock,
  } = product;

  // Pre-order items stay buyable even with zero stock. Consignment
  // availability (wigs at resellers) also keeps the card shoppable —
  // the API pre-computes `effectivelyInStock`; we fall back to the
  // local OR chain when the field is absent (e.g. static data).
  const effectiveInStock = effectivelyInStock !== undefined
    ? effectivelyInStock
    : preorderEnabled || inStock || consignmentAvailableQty > 0;

  // Determine automatic badge: Bestseller takes priority over New.
  // `isRecentlyCreated` MUST be deferred until after hydration. It used to
  // be computed inline with `Date.now()` during render, which caused
  // hydration mismatches (React errors #419/#422/#425) on /wigs and
  // /accessories/[slug] for products near the 14-day boundary — server's
  // Date.now() and client's Date.now() differ by enough ms that the
  // boolean can flip between SSR and hydration.
  const [isRecentlyCreated, setIsRecentlyCreated] = useState(false);
  useEffect(() => {
    if (createdAt) {
      const ageMs = Date.now() - new Date(createdAt).getTime();
      setIsRecentlyCreated(ageMs < 14 * 24 * 60 * 60 * 1000);
    }
  }, [createdAt]);
  const badgeType: "bestseller" | "new" | null = featured
    ? "bestseller"
    : (isNew || isRecentlyCreated)
      ? "new"
      : null;

  const t = useTranslations("product");
  const tc = useTranslations("common");
  const router = useRouter();
  const { addItem } = useCart();
  const { toast } = useToast();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const [justAdded, setJustAdded] = useState(false);
  const [heartPop, setHeartPop] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const addTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Audit Section 7 — cancel pending setJustAdded(false) on unmount so we
  // don't get a "set state on unmounted component" warning when the user
  // navigates away within the 1.5 s confirmation window.
  useEffect(() => () => {
    if (addTimerRef.current) clearTimeout(addTimerRef.current);
  }, []);
  const wishlisted = isInWishlist(product.id);

  const href = `/${category}/${slug}`;
  const hasDiscount = comparePrice && comparePrice > price;

  const handleQuickAdd = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addItem({
      productId: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      image: product.image || undefined,
      slug,
      categorySlug: category,
      isPreorder: preorderEnabled,
      preorderShipDate:
        preorderEnabled && product.preorderShipDate
          ? String(product.preorderShipDate)
          : undefined,
    });
    toast(t("addedToCartToast", { name: product.name }), "success");
    setJustAdded(true);
    if (addTimerRef.current) clearTimeout(addTimerRef.current);
    addTimerRef.current = setTimeout(() => setJustAdded(false), 1500);
  }, [addItem, product, preorderEnabled, toast, t]);

  const { layout } = useTheme();

  // Card style variants
  const cardStyleClasses = {
    flat: "bg-white shadow-sm hover:-translate-y-1 hover:shadow-xl",
    elevated: "bg-white shadow-md hover:-translate-y-2 hover:shadow-2xl",
    bordered: "bg-white border border-brand-dark/10 shadow-none hover:border-[var(--color-primary)] hover:-translate-y-1",
    glass: "bg-white/10 backdrop-blur-md border border-white/15 hover:border-white/30 hover:shadow-[var(--shadow-glow)] hover:-translate-y-1",
  };

  // Button radius based on theme
  const buttonRadiusClass =
    layout.buttonStyle === "pill"
      ? "rounded-full"
      : layout.buttonStyle === "rounded"
        ? "rounded-md"
        : "rounded-none";

  const handleWishlist = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
    if (!wishlisted) {
      setHeartPop(true);
      setTimeout(() => setHeartPop(false), 600);
    }
  }, [toggleWishlist, product.id, wishlisted]);

  return (
    <>
    <Link
      href={href}
      prefetch={false}
      onMouseEnter={() => router.prefetch(href)}
      onClick={() => trackEvent("product_card_click", { productId: product.id, productName: name, price, source: "grid" })}
      className={cn(
        // h-full so cards in the same grid row stretch to the tallest
        // sibling and their CTAs / price blocks line up horizontally.
        // Without this a 2-line product name sat higher than a 1-line
        // neighbor and the grid looked ragged.
        "group relative flex h-full flex-col overflow-hidden rounded-xl transition-all duration-500 ease-out will-change-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-offset-2",
        cardStyleClasses[layout.cardStyle],
        className
      )}
    >
      {/* Image container */}
      <div className="relative aspect-square w-full overflow-hidden rounded-t-xl bg-white">
        {image ? (
          <FallbackImage
            src={image}
            alt={
              category === "wigs"
                ? t("altPremiumWig", { name })
                : category === "toppers"
                  ? t("altHairTopper", { name })
                  : t("altAccessory", { name })
            }
            fill
            priority={priority}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            className="object-contain transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            fallbackClassName="absolute inset-0 bg-gradient-to-br from-brand-gold-light/30 via-brand-cream to-brand-gold/20"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-gold-light/30 via-brand-cream to-brand-gold/20" />
        )}

        {/* Hover overlay — Quick View */}
        {effectiveInStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-brand-dark/0 transition-colors duration-500 group-hover:bg-brand-dark/20">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowQuickView(true);
              }}
              className={cn("translate-y-4 bg-white/95 px-7 py-2.5 text-[11px] font-medium uppercase tracking-[0.2em] text-brand-dark opacity-0 shadow-md backdrop-blur-sm transition-all duration-500 ease-out group-hover:translate-y-0 group-hover:opacity-100 hover:bg-brand-gold hover:text-white cursor-pointer", buttonRadiusClass || "rounded-sm")}
            >
              {tc("quickView")}
            </button>
          </div>
        )}

        {/* Sold Out overlay with notify button — hidden for preorder items */}
        {!effectiveInStock && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/60 backdrop-blur-[1px]">
            <Badge variant="soldOut">{t("soldOut")}</Badge>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const email = window.prompt(t("notifyMeEnterEmail"));
                if (!email) return;
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                  toast(tc("invalidEmail"), "error");
                  return;
                }
                fetch("/api/back-in-stock", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email: email.trim().toLowerCase(), productId: product.id }),
                })
                  .then((r) => {
                    if (r.ok) {
                      toast(t("notifySignedUp"), "success");
                      trackEvent("back_in_stock_signup", { productId: product.id, productName: name });
                    } else {
                      toast(tc("failedToSubscribe"), "error");
                    }
                  })
                  .catch(() => toast(tc("failedToSubscribe"), "error"));
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/90 rounded-full text-[11px] font-medium uppercase tracking-wider text-brand-dark shadow-sm hover:bg-brand-gold hover:text-white transition-all duration-300"
            >
              <Bell className="h-3 w-3" />
              {t("notifyMe")}
            </button>
          </div>
        )}

        {/* Badges */}
        <div className="absolute start-3 top-3 flex flex-col gap-1.5">
          {preorderEnabled && (
            <span className="text-[11px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-sm bg-brand-gold text-white shadow-sm">
              {tc("preorder")}
            </span>
          )}
          {effectiveInStock && badgeType === "bestseller" && (
            <span className="text-[11px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-sm bg-amber-500 text-white">
              {tc("bestseller")}
            </span>
          )}
          {effectiveInStock && badgeType === "new" && (
            <span className="text-[11px] uppercase tracking-wider font-medium px-2.5 py-1 rounded-sm bg-emerald-600 text-white">
              {tc("new")}
            </span>
          )}
          {hasDiscount && effectiveInStock && <Badge variant="sale">{tc("sale")}</Badge>}
        </div>

        {/* Wishlist button — top right, appears on hover */}
        <button
          onClick={handleWishlist}
          aria-label={tc("wishlist")}
          className={cn(
            "absolute end-3 top-3 flex h-9 w-9 items-center justify-center rounded-full shadow-sm backdrop-blur-sm transition-all duration-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40",
            wishlisted
              ? "bg-brand-gold text-white opacity-100 hover:bg-brand-gold-dark"
              : "bg-white/80 text-brand-dark opacity-0 hover:bg-white hover:text-brand-gold hover:shadow-md group-hover:opacity-100 focus-visible:opacity-100"
          )}
        >
          <Heart className={cn(
            "h-4 w-4 transition-transform duration-300",
            wishlisted && "fill-current",
            heartPop && "animate-bounce-in"
          )} />
        </button>

        {/* Quick Add button — bottom, slides up on hover */}
        {effectiveInStock && (
          <div className="absolute bottom-0 start-0 end-0 translate-y-full transition-transform duration-500 ease-out group-hover:translate-y-0">
            <button
              onClick={handleQuickAdd}
              className={cn(
                "flex w-full items-center justify-center gap-2 py-3 text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold/40 focus-visible:ring-inset",
                justAdded
                  ? "bg-emerald-600"
                  : "bg-brand-dark hover:bg-brand-gold"
              )}
            >
              {justAdded ? (
                <>
                  <Check className="h-3.5 w-3.5" />
                  {tc("added")}
                </>
              ) : (
                <>
                  <ShoppingBag className="h-3.5 w-3.5" />
                  {preorderEnabled ? tc("preorder") : tc("quickAdd")}
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col px-3 py-3 sm:px-5 sm:py-5">
        <h3 className="font-display text-xs sm:text-sm tracking-tight text-brand-dark transition-colors duration-500 line-clamp-2 group-hover:text-brand-gold-dark md:text-base md:line-clamp-1">
          {name}
        </h3>

        <div className="mt-1.5 sm:mt-2.5 flex items-baseline gap-1.5 sm:gap-2.5 flex-wrap">
          <span
            className={cn(
              "font-display text-sm sm:text-base md:text-lg text-brand-dark",
              !effectiveInStock && "text-brand-muted"
            )}
          >
            {formatPrice(price)}
          </span>

          {hasDiscount && (
            <span className="text-[11px] sm:text-xs text-brand-muted/70 line-through">
              {formatPrice(comparePrice)}
            </span>
          )}
        </div>
      </div>
    </Link>

      {/* Quick View Modal — rendered outside the Link */}
      {showQuickView && (
        <Suspense fallback={null}>
          <QuickViewModal
            productId={product.id}
            onClose={() => setShowQuickView(false)}
          />
        </Suspense>
      )}
    </>
  );
});
